import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, Phone, X } from "lucide-react";
import { NAV_LINKS, SITE } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

const Logo = () => (
  <svg viewBox="0 0 64 64" className="h-9 w-9" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#064E3B" />
    <path
      d="M20 47 V31 a12 12 0 0 1 24 0 V47"
      fill="none"
      stroke="#FAF8F5"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <circle cx="32" cy="17" r="3.5" fill="#C59B27" />
    <path d="M25 47 h14" stroke="#C59B27" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
          scrolled
            ? "border-line bg-paper/90 backdrop-blur-md"
            : "border-transparent bg-paper/60 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6">
          <button
            data-testid="nav-logo"
            onClick={() => go("top")}
            className="flex items-center gap-3 text-left transition-opacity duration-200 hover:opacity-80"
          >
            <Logo />
            <span className="leading-tight">
              <span className="block font-serif text-[15px] font-semibold text-ink">
                London Empire Institution
              </span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-jade">
                Arabic · Wembley Park
              </span>
            </span>
          </button>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="text-[13px] font-medium text-ink-soft transition-colors duration-200 hover:text-pine"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              data-testid="nav-call-button"
              href={`tel:${SITE.phoneTel}`}
              className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-[13px] font-semibold text-ink transition-colors duration-200 hover:border-pine hover:text-pine"
            >
              <Phone className="h-3.5 w-3.5" />
              {SITE.phoneDisplay}
            </a>
            <a
              data-testid="nav-whatsapp-button"
              href={SITE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-pine px-4 py-2 text-[13px] font-semibold text-paper transition-colors duration-200 hover:bg-deep"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              WhatsApp
            </a>
          </div>

          <button
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-paper px-6 pb-10 pt-28 lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="border-b border-line py-4 text-left font-serif text-3xl text-ink transition-colors duration-200 hover:text-pine"
                >
                  {l.label}
                </button>
              ))}
            </nav>
            <div className="flex flex-col gap-3">
              <a
                href={`tel:${SITE.phoneTel}`}
                className="btn-ghost justify-center"
                data-testid="nav-call-button-mobile"
              >
                <Phone className="h-4 w-4" /> Call {SITE.phoneDisplay}
              </a>
              <a
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary justify-center"
                data-testid="nav-whatsapp-button-mobile"
              >
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
